import { UserCircleIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import React, { useState } from "react";
import { useInView } from "react-intersection-observer";
import styled from "styled-components";

import { useGetFile } from "src/shared/hooks";
import { HackerAPIFile } from "src/shared/utils/hackerapi";
import { invalidateSignedFileUrl } from "src/shared/utils/signedFileUrlCache";
import { Nullable } from "src/shared/utils/typescript";
import { ClockIcon } from "src/static/icons";
import {
  HackerDirectoryTeammateUserGrayIcon,
  HackerDirectoryTeammateUserWhiteIcon,
} from "src/static/img";
import { TeammateRelationship } from "src/views/hacker-directory/shared/types";

import { Container, PhotoFrame, TextStack } from "../styles";

const SquarePhotoFrame = styled(PhotoFrame)`
  position: relative;
  border-radius: 8px;
  background-color: #eff8fa;
  aspect-ratio: 1;
`;

const Photo = styled.img<{ $loaded: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 180ms ease;
`;

const PhotoLoadingPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  animation: photo-pulse 1.5s ease-in-out infinite;

  @keyframes photo-pulse {
    50% {
      opacity: 0.5;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StyledUserCircleIcon = styled(UserCircleIcon)`
  width: 50%;
  height: 50%;
  color: ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const TeammatesBadge = styled.span<{
  $variant: "looking" | "idle";
}>`
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: inline-flex;
  padding: 4px 9.189px;
  align-items: center;
  gap: 2px;
  border-radius: 27.568px;
  background: ${({ $variant, theme }) =>
    $variant === "looking"
      ? theme.globalConstants.color.backgroundPrimary
      : theme.globalConstants.color.backgroundGrayDark};
  color: ${({ $variant, theme }) =>
    $variant === "idle"
      ? theme.globalConstants.color.textSecondary
      : theme.globalConstants.color.white};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;

  > [data-expand] {
    max-width: 0;
    overflow: hidden;
    opacity: 0;
    white-space: nowrap;
    transition: max-width 180ms ease, opacity 180ms ease;
  }

  > [data-collapsed] {
    max-width: 16px;
    overflow: hidden;
    opacity: 1;
    transition: max-width 180ms ease, opacity 180ms ease;
  }

  @media (prefers-reduced-motion: reduce) {
    > [data-expand],
    > [data-collapsed] {
      transition: none;
    }
  }
`;

const RELATIONSHIP_BADGE_SIZE = 30;

const RelationshipBadge = styled.span<{
  $variant: "mutual" | "pending";
}>`
  position: absolute;
  right: 8px;
  bottom: 8px;
  box-sizing: border-box;
  display: inline-grid;
  grid-template-columns: minmax(0, auto) ${RELATIONSHIP_BADGE_SIZE}px;
  min-width: ${RELATIONSHIP_BADGE_SIZE}px;
  height: ${RELATIONSHIP_BADGE_SIZE}px;
  align-items: center;
  overflow: hidden;
  border-radius: ${RELATIONSHIP_BADGE_SIZE / 2}px;
  background: ${({ $variant, theme }) =>
    $variant === "mutual"
      ? theme.globalConstants.color.backgroundSuccess
      : "#F59E0B"};
  color: ${({ theme }) => theme.globalConstants.color.white};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
`;

const RelationshipLabel = styled.span`
  grid-column: 1;
  min-width: 0;
  max-width: 0;
  padding-right: 0;
  padding-left: 0;
  overflow: hidden;
  opacity: 0;
  white-space: nowrap;
  transition: max-width 180ms ease, padding 180ms ease, opacity 180ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const RelationshipIconFrame = styled.span`
  grid-column: 2;
  display: inline-flex;
  width: ${RELATIONSHIP_BADGE_SIZE}px;
  height: ${RELATIONSHIP_BADGE_SIZE}px;
  align-items: center;
  justify-content: center;

  > svg {
    display: block;
    width: 14px;
    height: 14px;
  }
`;

const InteractiveContainer = styled(Container).attrs({
  as: "button",
  type: "button",
})`
  box-sizing: border-box;
  padding: 4px;
  gap: 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  outline: none;
  transition: box-shadow 180ms ease, transform 180ms ease;

  &:focus-visible ${TeammatesBadge} {
    width: auto;
    padding: 4px 9.189px;
  }

  &:focus-visible ${RelationshipLabel} {
    max-width: 100px;
    padding-right: 2px;
    padding-left: 9.189px;
    opacity: 1;
  }

  &:focus-visible [data-expand] {
    max-width: 100px;
    opacity: 1;
  }

  &:focus-visible [data-collapsed] {
    max-width: 0;
    opacity: 0;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      box-shadow: 0 4px 12px rgb(0 0 0 / 8%);
      transform: translateY(-2px);
    }

    &:hover ${TeammatesBadge} {
      width: auto;
      padding: 4px 9.189px;
    }

    &:hover ${RelationshipLabel} {
      max-width: 100px;
      padding-right: 2px;
      padding-left: 9.189px;
      opacity: 1;
    }

    &:hover [data-expand] {
      max-width: 100px;
      opacity: 1;
    }

    &:hover [data-collapsed] {
      max-width: 0;
      opacity: 0;
    }
  }
`;

const TeammatesIconFrame = styled.span`
  display: inline-flex;
  width: 14px;
  height: 14px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const TeammatesIcon = styled.img`
  width: 9.8px;
  height: 10.5px;
`;

const Name = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
`;

const Interests = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  overflow-wrap: anywhere;
`;

const ProfileTextStack = styled(TextStack)`
  min-height: 44px;
  gap: 8px;
`;

const LiveProfileCell: React.FC<{
  profile: {
    name: Nullable<string>;
    profilePhoto: Nullable<HackerAPIFile>;
    interests: Nullable<string[]>;
    teammatesNeeded: Nullable<number>;
  };
  relationship: TeammateRelationship;
  onClick: React.MouseEventHandler<HTMLElement>;
}> = ({
  profile: { name, profilePhoto, interests, teammatesNeeded },
  relationship,
  onClick,
}) => {
  const photo = useGetFile(profilePhoto);
  const [photoFrameRef, photoInView] = useInView({
    rootMargin: "400px 0px",
    triggerOnce: true,
  });
  // IntersectionObserver is the deferral boundary. Once a card is nearby,
  // load it eagerly so native lazy-loading does not serialize the photo row.
  const [loadedPhotoUri, setLoadedPhotoUri] = useState<Nullable<string>>(null);
  const [failedPhotoUri, setFailedPhotoUri] = useState<Nullable<string>>(null);
  const photoUri = photo?.uri ?? null;
  const photoLoaded = photoUri != null && loadedPhotoUri === photoUri;
  const photoFailed = photoUri != null && failedPhotoUri === photoUri;
  const isLooking = teammatesNeeded != null && teammatesNeeded > 0;

  return (
    <InteractiveContainer
      aria-label={`View ${name || "hacker"}'s full profile`}
      data-teammate-relationship={relationship}
      onClick={onClick}
    >
      <SquarePhotoFrame ref={photoFrameRef}>
        {photoUri && !photoLoaded && !photoFailed && (
          <PhotoLoadingPlaceholder aria-hidden="true" />
        )}
        {photoUri && photoInView && !photoFailed && (
          <Photo
            $loaded={photoLoaded}
            src={photoUri}
            alt={name ? `${name}'s profile photo` : "Profile photo"}
            loading="eager"
            decoding="async"
            onLoad={() => setLoadedPhotoUri(photoUri)}
            onError={() => {
              if (photo?.id != null) {
                invalidateSignedFileUrl(photo.id);
              }
              setFailedPhotoUri(photoUri);
            }}
          />
        )}
        {(!photoUri || photoFailed) && <StyledUserCircleIcon aria-hidden />}
        {relationship === "mutual" || relationship === "pending" ? (
          <RelationshipBadge
            $variant={relationship}
            aria-label={
              relationship === "mutual"
                ? "Connected! You've both added each other."
                : "You added this hacker as a teammate. Waiting for them to add you back."
            }
          >
            <RelationshipLabel>
              {relationship === "mutual" ? "Teammate" : "Pending"}
            </RelationshipLabel>
            <RelationshipIconFrame>
              {relationship === "mutual" ? (
                <CheckIcon aria-hidden width={14} height={14} />
              ) : (
                <ClockIcon aria-hidden width={14} height={14} />
              )}
            </RelationshipIconFrame>
          </RelationshipBadge>
        ) : (
          teammatesNeeded != null && (
            <TeammatesBadge
              $variant={isLooking ? "looking" : "idle"}
              aria-label={
                isLooking
                  ? `Looking for ${teammatesNeeded} ${
                      teammatesNeeded === 1 ? "teammate" : "teammates"
                    }`
                  : "Not looking for teammates"
              }
            >
              <span data-collapsed>{teammatesNeeded}</span>
              <span data-expand>
                {isLooking ? `Looking for ${teammatesNeeded}` : "Not looking"}
              </span>
              <TeammatesIconFrame>
                <TeammatesIcon
                  src={
                    isLooking
                      ? HackerDirectoryTeammateUserWhiteIcon
                      : HackerDirectoryTeammateUserGrayIcon
                  }
                  alt=""
                  aria-hidden
                />
              </TeammatesIconFrame>
            </TeammatesBadge>
          )
        )}
      </SquarePhotoFrame>

      <ProfileTextStack>
        <Name>{name}</Name>
        {interests && interests.length > 0 && (
          <Interests>{interests.join(" · ")}</Interests>
        )}
      </ProfileTextStack>
    </InteractiveContainer>
  );
};

export default LiveProfileCell;
